import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import {
  PricingWizardState,
  PricingWizardActions,
  PricingWizardContextValue,
  PricingWizardFilters,
  UnitPricingInput,
  DEFAULT_FILTERS,
  DEFAULT_PRICING_INPUT,
  WIZARD_FILTERS_STORAGE_KEY,
} from '../types';

function loadFilters(): PricingWizardFilters {
  try {
    const stored = localStorage.getItem(WIZARD_FILTERS_STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_FILTERS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load pricing wizard filters:', e);
  }
  return DEFAULT_FILTERS;
}

function saveFilters(filters: PricingWizardFilters): void {
  try {
    localStorage.setItem(WIZARD_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.warn('Failed to save pricing wizard filters:', e);
  }
}

const initialState: PricingWizardState = {
  activeStep: 0,
  selectedUnitIds: new Set(),
  filters: DEFAULT_FILTERS,
  pricingMode: 'single',
  currentSingleUnitIndex: 0,
  unitPricingData: new Map(),
  draftLeaseIds: new Set(),
  selectedForApproval: new Set(),
};

export function usePricingWizardState(
  units: Unit[],
  leases: Lease[],
  draftLeases: Lease[] = []
): PricingWizardContextValue {
  const [state, setState] = useState<PricingWizardState>(initialState);
  const filtersLoaded = useRef(false);
  const draftsInitialized = useRef(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const stored = loadFilters();
    setState((prev) => ({
      ...prev,
      filters: stored,
    }));
    filtersLoaded.current = true;
  }, []);

  // Initialize from existing draft leases on page load (only once, before user interacts)
  useEffect(() => {
    // Only run once and only if user hasn't started selecting units yet
    if (draftsInitialized.current) return;
    if (draftLeases.length === 0) return;

    // Don't overwrite if user has already made selections
    if (state.selectedUnitIds.size > 0) {
      draftsInitialized.current = true;
      return;
    }

    draftsInitialized.current = true;

    // Find units that have draft leases and pre-populate state
    const unitIdsWithDrafts = new Set<string>();
    const pricingDataMap = new Map<string, UnitPricingInput>();
    const draftLeaseIdSet = new Set<string>();

    draftLeases.forEach((draft) => {
      unitIdsWithDrafts.add(draft.unitId);
      draftLeaseIdSet.add(draft.id);

      // Find the original lease for this unit to calculate rent increase
      const originalLease = leases.find(
        (l) => l.unitId === draft.unitId && l.status !== 'draft'
      );
      const originalRent = originalLease?.monthlyRent || draft.monthlyRent;
      const rentIncreasePercent =
        originalRent > 0
          ? Math.round(((draft.monthlyRent - originalRent) / originalRent) * 100 * 100) / 100
          : 0;

      // Calculate concession months from draft
      let concessionMonths = 0;
      if (Array.isArray(draft.concessions) && draft.concessions.length > 0) {
        concessionMonths = draft.concessions.reduce(
          (sum: number, c: any) => sum + (c.amount || 0),
          0
        );
      } else if (typeof draft.concessions === 'number') {
        concessionMonths = draft.concessions;
      }

      // Calculate lease length in months
      let leaseLengthMonths = 12;
      if (draft.leaseStartDate && draft.leaseEndDate) {
        const start = new Date(draft.leaseStartDate as any);
        const end = new Date(draft.leaseEndDate as any);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          leaseLengthMonths = Math.round(
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)
          );
        }
      }

      pricingDataMap.set(draft.unitId, {
        unitId: draft.unitId,
        rentIncreasePercent,
        concessionMonths,
        leaseLengthMonths: Math.max(leaseLengthMonths, 1),
        isPriced: true,
        draftLeaseId: draft.id,
      });
    });

    setState((prev) => ({
      ...prev,
      selectedUnitIds: unitIdsWithDrafts,
      unitPricingData: pricingDataMap,
      draftLeaseIds: draftLeaseIdSet,
    }));
  }, [draftLeases, leases, state.selectedUnitIds.size]);

  // Persist filters when they change
  useEffect(() => {
    if (filtersLoaded.current) {
      saveFilters(state.filters);
    }
  }, [state.filters]);

  // Navigation actions
  const setActiveStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, activeStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeStep: Math.min(prev.activeStep + 1, 3),
    }));
  }, []);

  const previousStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeStep: Math.max(prev.activeStep - 1, 0),
    }));
  }, []);

  // Step 1: Unit Selection actions
  const toggleUnitSelection = useCallback((unitId: string) => {
    setState((prev) => {
      const newSelected = new Set(prev.selectedUnitIds);
      if (newSelected.has(unitId)) {
        newSelected.delete(unitId);
      } else {
        newSelected.add(unitId);
      }
      return { ...prev, selectedUnitIds: newSelected };
    });
  }, []);

  const selectAllUnits = useCallback((unitIds: string[]) => {
    setState((prev) => ({
      ...prev,
      selectedUnitIds: new Set(unitIds),
    }));
  }, []);

  const clearUnitSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedUnitIds: new Set(),
    }));
  }, []);

  const setFilters = useCallback((filters: Partial<PricingWizardFilters>) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...filters },
    }));
  }, []);

  // Step 2: Pricing actions
  const setPricingMode = useCallback((mode: 'single' | 'batch') => {
    setState((prev) => ({ ...prev, pricingMode: mode }));
  }, []);

  const setCurrentSingleUnitIndex = useCallback((index: number) => {
    setState((prev) => ({ ...prev, currentSingleUnitIndex: index }));
  }, []);

  const updateUnitPricing = useCallback(
    (unitId: string, pricing: Partial<UnitPricingInput>) => {
      setState((prev) => {
        const newData = new Map(prev.unitPricingData);
        const existing = newData.get(unitId) || {
          ...DEFAULT_PRICING_INPUT,
          unitId,
        };
        newData.set(unitId, { ...existing, ...pricing });
        return { ...prev, unitPricingData: newData };
      });
    },
    []
  );

  const markUnitAsPriced = useCallback(
    (unitId: string, draftLeaseId: string) => {
      setState((prev) => {
        const newData = new Map(prev.unitPricingData);
        const existing = newData.get(unitId) || {
          ...DEFAULT_PRICING_INPUT,
          unitId,
        };
        newData.set(unitId, { ...existing, isPriced: true, draftLeaseId });

        const newDraftIds = new Set(prev.draftLeaseIds);
        newDraftIds.add(draftLeaseId);

        return {
          ...prev,
          unitPricingData: newData,
          draftLeaseIds: newDraftIds,
        };
      });
    },
    []
  );

  // Step 3 & 4: Review/Approval actions
  const addDraftLease = useCallback((leaseId: string) => {
    setState((prev) => {
      const newIds = new Set(prev.draftLeaseIds);
      newIds.add(leaseId);
      return { ...prev, draftLeaseIds: newIds };
    });
  }, []);

  const toggleApprovalSelection = useCallback((leaseId: string) => {
    setState((prev) => {
      const newSelected = new Set(prev.selectedForApproval);
      if (newSelected.has(leaseId)) {
        newSelected.delete(leaseId);
      } else {
        newSelected.add(leaseId);
      }
      return { ...prev, selectedForApproval: newSelected };
    });
  }, []);

  const selectAllForApproval = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedForApproval: new Set(prev.draftLeaseIds),
    }));
  }, []);

  const clearApprovalSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedForApproval: new Set(),
    }));
  }, []);

  const resetWizard = useCallback(() => {
    setState({
      ...initialState,
      filters: state.filters, // Keep filters
    });
  }, [state.filters]);

  // Build actions object
  const actions: PricingWizardActions = useMemo(
    () => ({
      setActiveStep,
      nextStep,
      previousStep,
      toggleUnitSelection,
      selectAllUnits,
      clearUnitSelection,
      setFilters,
      setPricingMode,
      setCurrentSingleUnitIndex,
      updateUnitPricing,
      markUnitAsPriced,
      addDraftLease,
      toggleApprovalSelection,
      selectAllForApproval,
      clearApprovalSelection,
      resetWizard,
    }),
    [
      setActiveStep,
      nextStep,
      previousStep,
      toggleUnitSelection,
      selectAllUnits,
      clearUnitSelection,
      setFilters,
      setPricingMode,
      setCurrentSingleUnitIndex,
      updateUnitPricing,
      markUnitAsPriced,
      addDraftLease,
      toggleApprovalSelection,
      selectAllForApproval,
      clearApprovalSelection,
      resetWizard,
    ]
  );

  // Derived data
  const selectedUnits = useMemo(() => {
    return units.filter((u) => state.selectedUnitIds.has(u.id));
  }, [units, state.selectedUnitIds]);

  const pricedUnitsCount = useMemo(() => {
    let count = 0;
    state.unitPricingData.forEach((data) => {
      if (data.isPriced) count++;
    });
    return count;
  }, [state.unitPricingData]);

  const totalSelectedCount = state.selectedUnitIds.size;

  const currentUnit = useMemo(() => {
    if (selectedUnits.length === 0) return null;
    const index = Math.min(state.currentSingleUnitIndex, selectedUnits.length - 1);
    return selectedUnits[index] || null;
  }, [selectedUnits, state.currentSingleUnitIndex]);

  return {
    state,
    actions,
    selectedUnits,
    pricedUnitsCount,
    totalSelectedCount,
    currentUnit,
  };
}
